import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, inject, ChangeDetectorRef, forwardRef } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SelectModule } from 'primeng/select';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { PropertiesEdit } from '../properties-edit/properties-edit';
import { descriptorEntriesToDict } from '@shared/utils/openapi-schema-utils';

@Component({
  selector: 'app-property-editor',
  standalone: true,
  imports: [
    FormsModule,
    InputTextModule,
    InputNumberModule,
    ToggleSwitchModule,
    SelectModule,
    CardModule,
    ButtonModule,
    // forwardRef — циклическая зависимость: PropertiesEdit импортит нас,
    // а мы рекурсивно рендерим его для type=object свойств.
    forwardRef(() => PropertiesEdit),
],
  templateUrl: './property-editor.html',
  styleUrl: './property-editor.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PropertyEditor implements OnInit, OnChanges {
  @Input() schema: any = {};
  @Input() value: any;
  @Input() title: string = '';  // Можно переопределить title из схемы
  @Input() subtitle?: string;  // Можно использовать для description из схемы
  @Input() disabled: boolean = false;
  @Output() valueChange = new EventEmitter<any>();

  private readonly cdr = inject(ChangeDetectorRef);

  isNull: boolean = false;
  enumOptions: {label: string, value: any}[] = [];
  hasError: boolean = false;
  errorMessage: string = '';

  ngOnChanges(changes: SimpleChanges) {
    if (changes['value'] && !changes['value'].firstChange) {
      const newValue = changes['value'].currentValue;
      this.isNull = newValue === null && this.isNullable;
      this.validateValue();
      this.cdr.markForCheck();
    }
  }

  ngOnInit() {
    // Use schema title/description if not provided as inputs
    if (!this.title && this.schema.title) {
      this.title = this.schema.title;
    }
    if (!this.subtitle && this.schema.description) {
      this.subtitle = this.schema.description;
    }

    // Check if value is null and property is nullable
    if (this.value === null && this.schema.nullable) {
      this.isNull = true;
    }

    // Prepare enum options if available
    if (this.schema.enum) {
      this.enumOptions = this.schema.enum.map((value: any, index: number) => {
        const label = this.schema['x-enumNames']?.[index] || String(value);
        return { label, value };
      });
    }

    // Set default value if value is undefined
    if (this.value === undefined && this.schema.default !== undefined) {
      this.value = this.schema.default;
      this.valueChange.emit(this.value);
    }

    // Validate initial value
    this.validateValue();
  }

  get propertyType(): string {
    return this.schema.type || 'string';
  }

  get isNullable(): boolean {
    return this.schema.nullable === true;
  }

  get isEnum(): boolean {
    return Array.isArray(this.schema.enum) && this.schema.enum.length > 0;
  }

  onValueChange(newValue: any) {
    this.value = newValue;
    this.validateValue();
    this.valueChange.emit(this.value);
    this.cdr.markForCheck();
  }

  validateValue() {
    // For non-string types, value must not be undefined/null/empty (unless nullable switch is on)
    if (!this.isNull) {
      const type = this.propertyType;

      if (type === 'integer' || type === 'number') {
        if (this.value === undefined || this.value === null || this.value === '') {
          this.hasError = true;
          this.errorMessage = $localize`:@@propertyEditor.error.enterValue:This field is required. Please enter a value.`;
          return;
        }
      } else if (type === 'boolean') {
        if (this.value === undefined || this.value === null) {
          this.hasError = true;
          this.errorMessage = $localize`:@@propertyEditor.error.selectValue:This field is required. Please select a value.`;
          return;
        }
      } else if (this.isEnum) {
        if (this.value === undefined || this.value === null) {
          this.hasError = true;
          this.errorMessage = $localize`:@@propertyEditor.error.selectValue:This field is required. Please select a value.`;
          return;
        }
      }
    }

    this.hasError = false;
    this.errorMessage = '';
  }

  onNullToggle(isNull: boolean) {
    this.isNull = isNull;
    if (isNull) {
      this.value = null;
      this.hasError = false;
      this.errorMessage = '';
    } else {
      // Set to default value or type-specific default
      if (this.schema.default !== undefined) {
        this.value = this.schema.default;
      } else {
        switch (this.propertyType) {
          case 'string':
            this.value = '';
            break;
          case 'integer':
          case 'number':
            this.value = 0;
            break;
          case 'boolean':
            this.value = false;
            break;
          case 'array':
            this.value = [];
            break;
          case 'object':
            this.value = {};
            break;
          default:
            if (this.isEnum && this.enumOptions.length > 0) {
              this.value = this.enumOptions[0].value;
            } else {
              this.value = '';
            }
        }
      }
    }
    this.validateValue();
    this.valueChange.emit(this.value);
    this.cdr.markForCheck();
  }

  /** Backend отдаёт nested object.properties как Array<{name, descriptor}>
   *  (тот же формат, что и top-level schema.properties). PropertiesEdit
   *  ожидает dict {name → schema} — нормализуем. Если уже dict — возвращаем
   *  как есть. */
  get objectPropertiesDict(): Record<string, any> {
    const p = this.schema?.properties;
    if (Array.isArray(p)) return descriptorEntriesToDict(p);
    return p && typeof p === 'object' ? p : {};
  }

  get arrayItemType(): string {
    return this.schema.items?.type || 'string';
  }

  get isArrayItemEnum(): boolean {
    return Array.isArray(this.schema.items?.enum) && this.schema.items.enum.length > 0;
  }

  get arrayItemEnumOptions(): { label: string; value: any }[] {
    const items = this.schema.items;
    if (!Array.isArray(items?.enum)) return [];
    const names = items['x-enumNames'] ?? [];
    return items.enum.map((value: any, index: number) => ({
      label: names[index] ?? String(value),
      value
    }));
  }

  get arrayItems(): any[] {
    if (!Array.isArray(this.value)) {
      return [];
    }
    return this.value;
  }

  addArrayItem() {
    const items = Array.isArray(this.value) ? [...this.value] : [];
    if (this.isArrayItemEnum) {
      const first = this.arrayItemEnumOptions[0];
      items.push(first ? first.value : null);
    } else {
      switch (this.arrayItemType) {
        case 'integer':
        case 'number':
          items.push(0);
          break;
        case 'boolean':
          items.push(false);
          break;
        default:
          items.push('');
      }
    }
    this.onValueChange(items);
  }

  removeArrayItem(index: number) {
    const items = [...this.arrayItems];
    items.splice(index, 1);
    this.onValueChange(items);
  }

  updateArrayItem(index: number, newValue: any) {
    const items = [...this.arrayItems];
    items[index] = newValue;
    this.onValueChange(items);
  }

  get minValue(): number | undefined {
    return this.schema.minimum;
  }

  get maxValue(): number | undefined {
    return this.schema.maximum;
  }

  get isNumericType(): boolean {
    const t = this.schema?.type;
    return t === 'integer' || t === 'number';
  }

  get step(): number {
    return this.propertyType === 'integer' ? 1 : 0.1;
  }

  get minFractionDigits(): number {
    return this.propertyType === 'integer' ? 0 : 0;
  }

  get maxFractionDigits(): number {
    return this.propertyType === 'integer' ? 0 : 10;
  }
}
